import { useState, useMemo, useRef } from "react";
import * as XLSX from "xlsx";
import DraggableModal from "./DraggableModal";
import SearchSelect from "./SearchSelect";
import FileDropZone from "./FileDropZone";
import { exportLocalToSupabase } from "../supabaseMigration";

/* GFI Nexus — Excel bulk importer.
 *
 * Reads a template workbook with sheets: Clients, Products, Shipments,
 * Invoices, Verification. Skips row 2 (instructions) and row 3 (gray
 * example). Empty rows are skipped. The Verification sheet is read
 * only for reporting and is never imported.
 *
 * All imported records are written as fully paid (balance = 0): bills
 * and invoices get status "paid" with a matching entry in gfi-payments
 * (outbound for bills, inbound for invoices). No follow-up step needed.
 *
 * Column matching is case- and whitespace-insensitive; multiple aliases
 * accepted so small template variations don't break the import. */

var T = {
  en: {
    title: "Import from Excel", subtitle: "Bulk-load clients, products, shipments, bills, invoices, expense bills, and export certificates from the GFI templates",
    pickFile: "Select Excel file (.xlsx)", picked: "Selected",
    season: "Import into season", noSeason: "(no season — auto-detect by date)",
    preview: "Detected", rowsLabel: "rows",
    clients: "Clients", products: "Products", shipments: "Shipments", invoices: "Invoices",
    bills: "Bills", expenseBills: "Expense Bills", exportCerts: "Export Certs",
    verification: "Verification (not imported)",
    importBtn: "Import", importing: "Importing…", cancel: "Cancel", close: "Close",
    summaryTitle: "Import complete",
    imported: "Imported", skipped: "Skipped duplicates", errorsLbl: "Errors",
    noFile: "Choose a .xlsx file first.",
    parseError: "Could not parse the file.",
    skipReason: "Skipped (duplicate)",
  },
  es: {
    title: "Importar desde Excel", subtitle: "Carga masiva de clientes, productos, embarques, bills, facturas, expense bills y certificados de exportación desde las plantillas GFI",
    pickFile: "Selecciona archivo Excel (.xlsx)", picked: "Seleccionado",
    season: "Importar a temporada", noSeason: "(sin temporada — auto-detectar por fecha)",
    preview: "Detectado", rowsLabel: "filas",
    clients: "Clientes", products: "Productos", shipments: "Embarques", invoices: "Facturas",
    bills: "Bills", expenseBills: "Expense Bills", exportCerts: "Certificados",
    verification: "Verificación (no se importa)",
    importBtn: "Importar", importing: "Importando…", cancel: "Cancelar", close: "Cerrar",
    summaryTitle: "Importación completada",
    imported: "Importados", skipped: "Duplicados omitidos", errorsLbl: "Errores",
    noFile: "Selecciona un archivo .xlsx primero.",
    parseError: "No se pudo leer el archivo.",
    skipReason: "Omitido (duplicado)",
  },
};

/* ───────────────── storage helpers ───────────────── */
function loadLS(k, fb) { try { var d = localStorage.getItem(k); return d ? JSON.parse(d) : fb; } catch { return fb; } }
function saveLS(k, v) { try { localStorage.setItem(k, JSON.stringify(v)); return true; } catch (e) { console.error("[Import] storage error", k, e); return false; } }

/* ───────────────── column matching ─────────────────
   Workbook headers come in many shapes ("Lot", "LOT #", "Lote", etc.).
   norm() collapses each candidate to lowercase + alphanumerics so the
   import can find a column without exactly matching the template. */
function norm(s) { return String(s || "").toLowerCase().replace(/[^a-z0-9]/g, ""); }
function pick(row, aliases) {
  var keys = Object.keys(row);
  for (var i = 0; i < aliases.length; i++) {
    var want = norm(aliases[i]);
    for (var j = 0; j < keys.length; j++) {
      if (norm(keys[j]) === want) return row[keys[j]];
    }
  }
  return undefined;
}
function str(v) { return v === undefined || v === null ? "" : String(v).trim(); }
function num(v) { var n = parseFloat(v); return isNaN(n) ? 0 : n; }
/* Percentage parser. Excel's "%" cell formatting silently converts
   "5%" to the underlying fraction 0.05 — when XLSX.sheet_to_json runs
   with raw:true it returns 0.05, which would be treated as 0.05% by
   downstream code (gross × 0.05/100). To match the displayed value
   regardless of cell formatting, any fractional reading 0 < n < 1 is
   rescaled to a whole number. Plain entries (5 → 5%, 50 → 50%) pass
   through unchanged. The narrow "0 < n < 1" trigger means a literal
   "0.5" entered as "0.5%" would be misread as 50% — that edge case is
   undocumented commission territory and the user is expected to enter
   either a whole-number rate or a percent-formatted cell. */
function pctRate(v) { var n = num(v); return n > 0 && n < 1 ? n * 100 : n; }

/* Excel serial dates → ISO YYYY-MM-DD. Strings pass through after a
   format normalization (mm/dd/yyyy or yyyy-mm-dd). */
function toISODate(v) {
  if (v === undefined || v === null || v === "") return "";
  if (typeof v === "number") {
    var d = XLSX.SSF.parse_date_code(v);
    if (!d) return "";
    var mm = String(d.m).padStart(2, "0");
    var dd = String(d.d).padStart(2, "0");
    return d.y + "-" + mm + "-" + dd;
  }
  var s = String(v).trim();
  var m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (m) {
    var yr = m[3].length === 2 ? "20" + m[3] : m[3];
    return yr + "-" + m[1].padStart(2, "0") + "-" + m[2].padStart(2, "0");
  }
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  var d2 = new Date(s);
  if (!isNaN(d2.getTime())) return d2.toISOString().substring(0, 10);
  return "";
}

/* Parses payment term ("21", "Net 30", "Upon Settlement", etc.) and adds
   the result to an ISO base date. Used to auto-fill Due Date during
   import when the column is left blank. Fallback is 21 days. */
function parseTermDays(term) {
  if (term === undefined || term === null || term === "") return null;
  var m = String(term).match(/(\d+)/);
  return m ? parseInt(m[1], 10) : null;
}
function addDaysToISO(isoDate, days) {
  if (!isoDate) return "";
  var d = new Date(isoDate + "T00:00:00");
  if (isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + (parseInt(days, 10) || 0));
  return d.toISOString().substring(0, 10);
}

/* ───────────────── sheet → rows ─────────────────
   Use sheet_to_json with header:1 so we control header detection. Row 1
   is the header. Rows 2 (instructions) and 3 (gray example) are skipped
   per the template spec. Empty rows are filtered out. */
function readRows(workbook, sheetName) {
  var sheet = workbook.Sheets[sheetName];
  if (!sheet) return [];
  var all = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
  if (all.length < 2) return [];
  var headers = all[0].map(function (h) { return str(h); });
  var dataRows = all.slice(3); /* skip header(0), instructions(1=row2), example(2=row3) */
  return dataRows
    .map(function (arr) {
      var obj = {};
      headers.forEach(function (h, i) { if (h) obj[h] = arr[i]; });
      return obj;
    })
    .filter(function (obj) {
      return Object.values(obj).some(function (v) { return str(v) !== ""; });
    });
}

/* ───────────────── id generators ───────────────── */
function nextIdSeq(items, prefix, pad) {
  var nums = (items || []).map(function (i) {
    var m = String(i.id || "").match(new RegExp("^" + prefix + "(\\d+)$"));
    return m ? parseInt(m[1], 10) : 0;
  });
  return prefix + String((nums.length ? Math.max.apply(null, nums) : 0) + 1).padStart(pad || 4, "0");
}
function nextShipmentId(ships) { return nextIdSeq(ships, "SHP-", 4); }
function nextInvoiceId(invs) { return nextIdSeq(invs, "INV-", 4); }
function nextCertId(certs) { return nextIdSeq(certs, "EXC-", 4); }
function nextBillId(bills) { return nextIdSeq(bills, "BILL-", 4); }
function nextExpenseBillId(items) { return nextIdSeq(items, "EXP-", 4); }
function nextPaymentId(items) { return nextIdSeq(items, "PAY-", 4); }
function nextClientId(items) {
  var nums = (items || []).map(function (i) { return parseInt(i.id, 10); }).filter(function (n) { return !isNaN(n); });
  return Date.now() + (nums.length ? Math.max.apply(null, nums) : 0) + Math.floor(Math.random() * 1000);
}
function nextProductId(items) {
  var nums = (items || []).map(function (i) {
    var m = String(i.productCode || "").match(/PRD-(\d+)/);
    return m ? parseInt(m[1], 10) : 0;
  });
  return "PRD-" + String((nums.length ? Math.max.apply(null, nums) : 0) + 1).padStart(4, "0");
}

/* SKU pattern matches ProductRegistry.mkSKU exactly:
   GFI-[PRODUCT_CODE]-[SIZE][CATEGORY][LABEL_INITIALS]. */
var PMAP = { Blackberry: "BLK", Blueberry: "BLU", Raspberry: "RSP", Strawberry: "STR", Avocado: "AVO", Lime: "LIM" };
var CMAP = { Organic: "OG", Conventional: "CV", OG: "OG", CV: "CV" };
function buildSku(p) {
  var pc = PMAP[p.product] || (p.product ? p.product.slice(0, 3).toUpperCase() : "XXX");
  var sz = (p.size || "").replace(/\s/g, "").toUpperCase();
  return "GFI-" + pc + "-" + sz + (CMAP[p.category] || "XX") + (p.labelInitials || "XX");
}

/* ───────────────── parse phase ─────────────────
   Each parse function returns { rows, count } where rows is a clean
   array ready for the import phase. detectCounts() runs all parsers
   without writing to localStorage so the preview can display row totals
   before the user commits. */
function parseClientsRows(rows) {
  return rows.map(function (r) {
    return {
      shortName: str(pick(r, ["Short Name", "ShortName", "Short"])),
      fullName: str(pick(r, ["Full Name", "FullName", "Full"])),
      initials: str(pick(r, ["Initials", "Iniciales"])),
      dba: str(pick(r, ["DBA"])),
      taxId: str(pick(r, ["Tax ID", "EIN", "TaxID"])),
      phoneNumber: str(pick(r, ["Phone", "Telefono", "Teléfono"])),
      email: str(pick(r, ["Email", "Correo"])),
      address: str(pick(r, ["Address", "Direccion", "Dirección"])),
      city: str(pick(r, ["City", "Ciudad"])),
      state: str(pick(r, ["State", "Estado"])),
      zip: str(pick(r, ["Zip", "Zip Code", "CP"])),
      country: str(pick(r, ["Country", "Pais", "País"])) || "United States",
      paymentTerm: str(pick(r, ["Term", "Payment Term", "Condición de Pago"])),
    };
  });
}

function parseProductsRows(rows) {
  return rows.map(function (r) {
    var product = str(pick(r, ["Product", "Producto"]));
    var sizeRaw = str(pick(r, ["Size", "Tamaño"]));
    var unitsPerCase = num(pick(r, ["Units/Case", "Units per Case", "Units", "UnitsCase"]));
    var category = str(pick(r, ["Category", "Categoría"]));
    var labelInitials = str(pick(r, ["Label Initials", "Initials", "Label"])).toUpperCase().slice(0, 5);
    return {
      product: product, size: sizeRaw, unitsPerCase: unitsPerCase,
      casesPerPallet: num(pick(r, ["Cases/Pallet", "Cases per Pallet", "CasesPallet"])),
      packType: str(pick(r, ["Pack Type", "Packaging"])) || "clamshell",
      label: str(pick(r, ["Label Name", "Label"])),
      labelInitials: labelInitials,
      category: CMAP[category] === "OG" ? "Organic" : CMAP[category] === "CV" ? "Conventional" : category,
    };
  });
}

function parseShipmentsRows(rows) {
  return rows.map(function (r) {
    /* Departure / Arrival are independent columns now. The legacy single
       "Date" column is still accepted as a fallback for both. */
    var legacyDate = toISODate(pick(r, ["Date", "Bill Date", "Shipment Date", "Fecha"]));
    return {
      dateDeparture: toISODate(pick(r, ["Departure", "Departure (MX)", "Departure MX", "Salida"])) || legacyDate,
      dateArrival: toISODate(pick(r, ["Arrival", "Arrival (US)", "Arrival US", "Llegada"])) || legacyDate,
      dueDate: toISODate(pick(r, ["Due Date", "Fecha de Pago"])),
      lot: str(pick(r, ["Lot", "LOT", "Lote"])),
      shipperName: str(pick(r, ["Shipper", "Supplier", "Shipper Name", "Proveedor"])),
      shipperInitials: str(pick(r, ["Shipper Initials", "Shipper Code"])),
      proforma: str(pick(r, ["Proforma", "Proforma #"])),
      shipperInvoice: str(pick(r, ["Shipper Invoice", "Invoice #"])),
      entryNumber: str(pick(r, ["Entry #", "Entry Number", "Entry"])),
      warehouse: str(pick(r, ["Warehouse", "Bodega", "Almacén"])),
      productName: str(pick(r, ["Product", "Producto"])),
      productDescription: str(pick(r, ["Description", "Product Description"])),
      /* Excel column aliases — keep legacy "Presentation/Presentación"
         so older exports still import; the user-visible label was
         renamed to "Product Description / Descripción del Producto"
         so we also accept those names. The importer maps this →
         line.productDescription. */
      presentation: str(pick(r, ["Product Description", "Descripción del Producto", "Presentation", "Item/Pack/Label", "Presentación"])),
      category: str(pick(r, ["Category", "Categoría"])),
      cases: num(pick(r, ["Cases", "Cajas"])),
      pallets: num(pick(r, ["Pallets", "Tarimas"])),
      reportedPrice: num(pick(r, ["Reported Price", "Price (ref)", "Price(ref)", "Precio (ref)", "Price", "Precio"])),
      exportCert: str(pick(r, ["Export Cert", "Export Certificate", "Certificate", "Certificado", "Cert", "Cert #", "Cert Number", "Certificate #"])),
      saleStatus: normalizeSaleStatus(pick(r, ["Sale Status", "Status de Venta", "Sale"])),
      totalFees: num(pick(r, ["Total Fees", "Fees", "Deducciones"])),
      commissionRate: num(pick(r, ["Commission %", "Commission Rate", "Commission"])) || 5,
      totalPaidToShipper: num(pick(r, ["Total Paid to Shipper", "Amount Paid", "Total Paid", "Amount Due"])),
      paymentDate: toISODate(pick(r, ["Payment Date", "Paid Date", "Settled Date"])),
      paymentMethod: str(pick(r, ["Payment Method", "Method"])) || "wire",
      paymentReference: str(pick(r, ["Payment Ref", "Payment Reference", "Reference"])),
    };
  });
}

function parseInvoicesRows(rows) {
  return rows.map(function (r) {
    /* Presentation overrides plain Description when both columns exist
       (matches the Shipments parser behavior). */
    var legacyDesc = str(pick(r, ["Description", "Product Description"]));
    /* Up to 2 inline fee slots per invoice row. The companion "Invoice
       Fees" sheet (vertical, one fee per row, keyed by PU#) can also
       contribute fees — both sources are unioned by the importer. The
       single legacy `Fees` column still works as a final fallback. */
    var inlineFees = [];
    for (var i = 1; i <= 2; i++) {
      var ft = str(pick(r, ["Fee Type " + i]));
      var qty = pick(r, ["Fee Qty " + i]);
      var rate = pick(r, ["Fee Rate " + i]);
      var amt = pick(r, ["Fee Amount " + i]);
      var billRef = str(pick(r, ["Fee → Bill " + i, "Fee Bill " + i, "Fee Linked Bill " + i]));
      var hasAny = ft || (qty !== undefined && qty !== "") || (rate !== undefined && rate !== "") || (amt !== undefined && amt !== "") || billRef;
      if (!hasAny) continue;
      var qtyN = num(qty); var rateN = num(rate); var amtN = num(amt);
      if (!amtN && qtyN && rateN) amtN = qtyN * rateN;
      /* Normalize to positive magnitude. Arturo's Excel sometimes types
         deduction amounts as negative (e.g. -384.66 for an Advance
         Financing Fee). The runtime calc applies the SUBTRACT sign
         itself, so a stored negative would double-flip and inflate the
         total. Mirror this in parseInvoiceFeesRows below. */
      amtN = Math.abs(amtN);
      inlineFees.push({ feeType: ft || "Other", qty: qtyN || 1, rate: rateN, amount: amtN, billRef: billRef });
    }
    return {
      date: toISODate(pick(r, ["Date", "Invoice Date", "Fecha"])),
      dueDate: toISODate(pick(r, ["Due Date", "Fecha Vencimiento"])),
      puNumber: str(pick(r, ["PU#", "PU Number", "Invoice #", "Invoice Number"])),
      lot: str(pick(r, ["Lot", "LOT"])),
      clientName: str(pick(r, ["Client", "Customer", "Cliente"])),
      clientRef: str(pick(r, ["Customer Ref", "Customer PO", "Client Ref"])),
      productName: str(pick(r, ["Product", "Producto"])),
      productDescription: str(pick(r, ["Product Description", "Descripción del Producto", "Presentation", "Item/Pack/Label", "Presentación"])) || legacyDesc,
      category: str(pick(r, ["Category", "Categoría", "Cat"])),
      shipperName: str(pick(r, ["Shipper", "Supplier", "Proveedor"])),
      cases: num(pick(r, ["Cases", "Cajas"])),
      pallets: num(pick(r, ["Pallets", "Tarimas"])),
      salePrice: num(pick(r, ["Sale Price", "Price", "Precio"])),
      saleStatus: normalizeSaleStatus(pick(r, ["Sale Status", "Status de Venta", "Status"])),
      fees: inlineFees,
      feesTotal: num(pick(r, ["Fees", "Deductions", "Deducciones"])),
      /* Client commission %. Uses pctRate() (the same helper that fixed
         the Bills double-division bug): if the Excel cell is plain
         (10 → 10%), passes through; if Excel formats as "10%" (stored
         underlying as 0.10), rescales to 10. The downstream calc in
         the Invoices import block then does gross × rate / 100, so the
         single division happens exactly once. */
      commissionRate: pctRate(pick(r, ["Commission %", "Commission Rate", "Commission"])),
      totalPaid: num(pick(r, ["Total Paid", "Amount Paid"])),
      paymentDate: toISODate(pick(r, ["Payment Date", "Paid Date", "Deposit Date"])),
      paymentMethod: str(pick(r, ["Payment Method", "Method"])) || "wire",
      paymentReference: str(pick(r, ["Payment Ref", "Payment Reference", "Reference"])),
    };
  });
}

/* Vertical fee sheet for invoices — one fee per row, joined to the
   parent invoice by PU#. Mirrors the Bills "Extra Fees" pattern. */
function parseInvoiceFeesRows(rows) {
  return rows.map(function (r) {
    var qty = num(pick(r, ["Fee Qty", "Qty"]));
    var rate = num(pick(r, ["Fee Rate", "Rate"]));
    var amt = pick(r, ["Fee Amount", "Amount"]);
    var amount = num(amt);
    if (!amount && qty && rate) amount = qty * rate;
    /* See parseInvoicesRows note: store positive magnitude; the runtime
       applies the SUBTRACT sign once. */
    amount = Math.abs(amount);
    return {
      puNumber: str(pick(r, ["PU#", "PU Number", "Invoice #", "Invoice Number"])),
      feeType: str(pick(r, ["Fee Type"])),
      qty: qty || 1,
      rate: rate,
      amount: amount,
      billRef: str(pick(r, ["Fee → Bill", "Fee Bill", "Linked Bill", "Bill"])),
    };
  });
}

/* ──────── Export Certificates ──────── */
function parseExportCertsRows(rows) {
  return rows.map(function (r) {
    /* Keep `available` raw so the importer can distinguish "blank" (use
       totalInvoices as the default) from an explicit zero. */
    return {
      certNumber: str(pick(r, ["Certificate #", "Certificate", "Cert #", "Certificado"])),
      shipperName: str(pick(r, ["Shipper", "Supplier", "Proveedor"])),
      product: str(pick(r, ["Product", "Producto"])),
      certifyingBody: str(pick(r, ["Certifying Body", "Certificador", "Certifier"])),
      periodFrom: toISODate(pick(r, ["Period From", "Period from", "Desde", "Start"])),
      periodTo: toISODate(pick(r, ["Period To", "Period to", "Hasta", "End"])),
      totalInvoices: num(pick(r, ["Total Invoices", "Total", "Total Invoices / containers"])),
      available: pick(r, ["Available", "Disponible"]),
      notes: str(pick(r, ["Notes", "Notas"])),
    };
  });
}
function parseAssignedInvoicesRows(rows) {
  return rows.map(function (r) {
    return {
      certNumber: str(pick(r, ["Certificate #", "Certificate", "Cert #", "Certificado"])),
      shipperInvoice: str(pick(r, ["Shipper Invoice #", "Shipper Invoice", "Invoice #"])),
      date: toISODate(pick(r, ["Date", "Fecha"])),
    };
  });
}

/* ──────── Shipment Bills ──────── */
/* Map the human-readable "Sale Status" cell to the canonical key used
   throughout BillRegistry (goodDelivery, protection, pas, samples,
   dumped, donated, transfer). Matching reuses `norm()` so case and
   punctuation differences ("good delivery", "Good-Delivery") all
   collapse to the same key. Unknown values pass through trimmed so
   no data is silently dropped. */
function normalizeSaleStatus(v) {
  var raw = str(v);
  if (!raw) return "";
  var key = norm(raw);
  var map = {
    gooddelivery: "goodDelivery",
    protection: "protection",
    pas: "pas",
    sample: "samples",
    samples: "samples",
    dumped: "dumped",
    donated: "donated",
    transfer: "transfer",
  };
  return map[key] || raw;
}
function parseBillsRows(rows) {
  return rows.map(function (r) {
    /* Up to 3 inline fee slots per row. Empty slots are dropped. */
    var fees = [];
    for (var i = 1; i <= 3; i++) {
      var ft = str(pick(r, ["Fee Type " + i]));
      var qty = pick(r, ["Fee Qty " + i]);
      var rate = pick(r, ["Fee Rate " + i]);
      var amt = pick(r, ["Fee Amount " + i]);
      var hasAny = ft || (qty !== undefined && qty !== "") || (rate !== undefined && rate !== "") || (amt !== undefined && amt !== "");
      if (!hasAny) continue;
      var qtyN = num(qty); var rateN = num(rate); var amtN = num(amt);
      if (!amtN && qtyN && rateN) amtN = qtyN * rateN;
      fees.push({ feeType: ft || "Other", qty: qtyN || 1, rate: rateN, amount: amtN });
    }
    return {
      lot: str(pick(r, ["Lot", "LOT", "Lote"])),
      shipperName: str(pick(r, ["Shipper", "Supplier", "Proveedor"])),
      date: toISODate(pick(r, ["Bill Date", "Date", "Fecha"])),
      dueDate: toISODate(pick(r, ["Due Date", "Fecha de Pago"])),
      proforma: str(pick(r, ["Proforma", "Proforma #"])),
      shipperInvoice: str(pick(r, ["Shipper Invoice", "Invoice #"])),
      entryNumber: str(pick(r, ["Entry #", "Entry Number", "Entry"])),
      exportCert: str(pick(r, ["Export Cert", "Export Certificate", "Certificate", "Certificado", "Cert", "Cert #", "Cert Number", "Certificate #"])),
      reference: str(pick(r, ["Reference", "Referencia"])),
      productName: str(pick(r, ["Product", "Producto"])),
      productDescription: str(pick(r, ["Product Description", "Description", "Descripción del Producto", "Descripción", "Presentation", "Presentación", "Item/Pack/Label"])),
      category: str(pick(r, ["Cat", "Category", "Categoría"])),
      cases: num(pick(r, ["Cases", "Cajas"])),
      reportedPrice: num(pick(r, ["Reported Price", "Price", "Precio"])),
      amount: pick(r, ["Amount", "Importe"]),
      saleStatus: normalizeSaleStatus(pick(r, ["Sale Status", "Status"])),
      commissionRate: pctRate(pick(r, ["Commission %", "Commission Rate"])),
      commissionAmount: pick(r, ["Commission $", "Commission Amount"]),
      fees: fees,
      /* Only the explicit "Less Amount Paid" column counts. The earlier
         "Advance" alias was removed because users keep an "Advance"
         column for unrelated documentation (Twin River weekly $4/case
         tracking) and the importer was misreading it as a payment. */
      lessAmountPaid: num(pick(r, ["Less Amount Paid"])),
      amountDue: num(pick(r, ["Amount Due"])),
    };
  });
}
function parseExtraFeesRows(rows) {
  return rows.map(function (r) {
    var qty = num(pick(r, ["Fee Qty", "Qty"]));
    var rate = num(pick(r, ["Fee Rate", "Rate"]));
    var amt = pick(r, ["Fee Amount", "Amount"]);
    var amount = num(amt);
    if (!amount && qty && rate) amount = qty * rate;
    return {
      lot: str(pick(r, ["Lot", "LOT", "Lote"])),
      feeType: str(pick(r, ["Fee Type"])),
      qty: qty || 1,
      rate: rate,
      amount: amount,
    };
  });
}

/* Vertical fee sheet for expense bills — one fee per row, joined to
   the parent bill by Reference (and optionally Vendor when the same
   reference is used by multiple vendors). Mirrors Bills "Extra Fees". */
function parseExpenseFeesRows(rows) {
  return rows.map(function (r) {
    var qty = num(pick(r, ["Fee Qty", "Qty"]));
    var rate = num(pick(r, ["Fee Rate", "Rate"]));
    var amt = pick(r, ["Fee Amount", "Amount"]);
    var amount = num(amt);
    if (!amount && qty && rate) amount = qty * rate;
    return {
      reference: str(pick(r, ["Reference", "Referencia", "Ref"])),
      vendorName: str(pick(r, ["Vendor", "From", "Proveedor"])),
      feeType: str(pick(r, ["Fee Type"])),
      qty: qty || 1,
      rate: rate,
      amount: amount,
      lot: str(pick(r, ["Lot", "LOT", "Lote"])),
      billRef: str(pick(r, ["Bill", "Bill ID", "Bill Lot"])),
    };
  });
}

/* ──────── Expense Bills ──────── */
function parseExpenseBillsRows(rows) {
  return rows.map(function (r) {
    /* Up to 3 fee/lot/bill triples per row. Each fee can optionally be
       tied to a specific lot and bill reference for distribution. */
    var fees = [];
    for (var i = 1; i <= 3; i++) {
      var ft = str(pick(r, ["Fee Type " + i]));
      var qty = pick(r, ["Fee Qty " + i]);
      var rate = pick(r, ["Fee Rate " + i]);
      var amt = pick(r, ["Fee Amount " + i]);
      var lot = str(pick(r, ["Lot " + i]));
      var billRef = str(pick(r, ["Bill " + i]));
      var hasAny = ft || (qty !== undefined && qty !== "") || (rate !== undefined && rate !== "") || (amt !== undefined && amt !== "") || lot || billRef;
      if (!hasAny) continue;
      var qtyN = num(qty); var rateN = num(rate); var amtN = num(amt);
      if (!amtN && qtyN && rateN) amtN = qtyN * rateN;
      fees.push({ feeType: ft || "Other", qty: qtyN || 1, rate: rateN, amount: amtN, lot: lot, billRef: billRef });
    }
    return {
      vendorName: str(pick(r, ["Vendor", "From", "Proveedor"])),
      reference: str(pick(r, ["Reference", "Referencia", "Ref"])),
      category: str(pick(r, ["Category", "Categoría"])),
      date: toISODate(pick(r, ["Date", "Fecha"])),
      dueDate: toISODate(pick(r, ["Due Date", "Fecha de Pago"])),
      fees: fees,
    };
  });
}

/* ───────────────── import phase ─────────────────
   importAll() commits everything in one shot: reads existing localStorage
   for each entity, appends new records, writes back. Returns counts so
   the modal can show a summary.

   Order is significant: Clients → Products → Shipments → Export Certs →
   Bills → Invoices → Expense Bills → Payments. Each block can resolve
   references against entities loaded earlier (e.g. Bills resolve
   shipperName against gfi-suppliers and lot against the just-imported
   shipments; Invoices resolve clientName against gfi-clients, lot
   against gfi-shipments / gfi-bills, etc.).

   Duplicate detection per entity:
     - clients      → initials OR shortName
     - products     → SKU
     - shipments    → lot
     - export certs → certNumber
     - bills        → lot
     - invoices     → puNumber
     - expense bills → vendor + reference + date

   Every record imported is stamped as fully paid (balance = 0). Bills
   and Invoices get a matching entry in gfi-payments + their .payments[]
   array when the Excel row carried payment metadata (totalPaid,
   paymentDate, paymentMethod, paymentReference). */
function importAll(parsed, seasonId) {
  var summary = { clients: 0, products: 0, shipments: 0, invoices: 0, bills: 0, expenseBills: 0, exportCerts: 0, skipped: 0 };

  /* Shared catalog reads + lookup helpers. Used by Shipments, Bills,
     Invoices and Expense Bills blocks to resolve names → ids without
     duplicate localStorage hits. */
  function nrm(s) { return String(s || "").toLowerCase().trim(); }
  var supList = loadLS("gfi-suppliers", []);
  var cliList = loadLS("gfi-clients", []);
  var whList = loadLS("gfi-warehouses", []);
  var seasonList = loadLS("gfi-seasons", []);
  function findSupplier(name) {
    if (!name) return null;
    var n = nrm(name);
    return supList.find(function (s) { return nrm(s.shortName) === n || nrm(s.fullName) === n || nrm(s.name) === n || nrm(s.companyName) === n; }) || null;
  }
  function findClient(name) {
    if (!name) return null;
    var n = nrm(name);
    return cliList.find(function (c) { return nrm(c.shortName) === n || nrm(c.fullName) === n || nrm(c.name) === n || nrm(c.companyName) === n; }) || null;
  }
  function findWarehouse(name) {
    if (!name) return null;
    var n = nrm(name);
    return whList.find(function (w) { return nrm(w.shortName) === n || nrm(w.fullName) === n || nrm(w.name) === n || nrm(w.initials) === n; }) || null;
  }
  function findSeason(isoDate) {
    if (!isoDate) return null;
    var d = new Date(isoDate + "T00:00:00");
    return seasonList.find(function (s) {
      if (!s.startDate || !s.endEstimate) return false;
      return new Date(s.startDate + "T00:00:00") <= d && d <= new Date(s.endEstimate + "T00:00:00");
    }) || null;
  }
  function isoWeek(isoDate) {
    if (!isoDate) return "";
    var d = new Date(isoDate + "T00:00:00"); d.setHours(0, 0, 0, 0);
    d.setDate(d.getDate() + 3 - ((d.getDay() + 6) % 7));
    var w1 = new Date(d.getFullYear(), 0, 4);
    return String(1 + Math.round(((d - w1) / 86400000 - 3 + ((w1.getDay() + 6) % 7)) / 7)).padStart(2, "0");
  }

  /* CLIENTS */
  var existingClients = loadLS("gfi-clients", []);
  var clientByInitials = {};
  var clientByShort = {};
  existingClients.forEach(function (c) {
    if (c.initials) clientByInitials[c.initials.toUpperCase()] = c;
    if (c.shortName) clientByShort[c.shortName.toLowerCase()] = c;
  });
  parsed.clients.forEach(function (r) {
    var dupKey = r.initials && clientByInitials[r.initials.toUpperCase()];
    var dupShort = r.shortName && clientByShort[r.shortName.toLowerCase()];
    if (dupKey || dupShort) { summary.skipped++; return; }
    var c = {
      id: nextClientId(existingClients),
      initials: r.initials,
      shortName: r.shortName,
      fullName: r.fullName || r.shortName,
      dba: r.dba,
      taxId: r.taxId,
      phoneCountry: "1", phoneArea: "", phoneNumber: r.phoneNumber,
      email: r.email,
      address: r.address, city: r.city, state: r.state, zip: r.zip, country: r.country,
      warehouseId: "",
      deliveryAddress: "", deliveryCity: "", deliveryState: "", deliveryZip: "", deliveryAttention: "", deliveryPhone: "",
      paymentTerm: r.paymentTerm,
      contacts: [], documents: [],
      isActive: true,
    };
    existingClients.push(c);
    if (c.initials) clientByInitials[c.initials.toUpperCase()] = c;
    if (c.shortName) clientByShort[c.shortName.toLowerCase()] = c;
    summary.clients++;
  });
  saveLS("gfi-clients", existingClients);

  /* PRODUCTS */
  var existingProducts = loadLS("gfi-products", []);
  var skuSet = {};
  existingProducts.forEach(function (p) { if (p.sku) skuSet[p.sku] = true; });
  parsed.products.forEach(function (r) {
    if (!r.product) { summary.skipped++; return; }
    var sku = buildSku(r);
    if (skuSet[sku]) { summary.skipped++; return; }
    var p = {
      id: Date.now() + Math.floor(Math.random() * 1000) + summary.products,
      productCode: nextProductId(existingProducts),
      product: r.product, size: r.size, unitsPerCase: r.unitsPerCase, casesPerPallet: r.casesPerPallet,
      packType: r.packType, label: r.label, labelInitials: r.labelInitials, category: r.category,
      sku: sku,
      description: r.product + " " + r.unitsPerCase + "/" + r.size + " " + (CMAP[r.category] || "") + " " + r.labelInitials + " Label",
      isActive: true,
    };
    existingProducts.push(p);
    skuSet[sku] = true;
    summary.products++;
  });
  saveLS("gfi-products", existingProducts);

  /* SHIPMENTS — multi-line aware. All Excel rows that share the same Lot
     fold into ONE gfi-shipments record, with one product line per row.
     Header fields (Lot, Departure, Arrival, Shipper, Proforma, Shipper
     Invoice, Entry, Warehouse) come from the FIRST row of the group; per-
     row fields (Product, Presentation, Category, Cases, Pallets, Reported
     Price, Export Cert, Sale Status) become individual lines. supplierId,
     warehouseId, exportCertId, seasonId and week are resolved/computed
     from the catalogs in localStorage — no caller-supplied seasonId is
     used here. Lot duplicates against the existing store are skipped as
     a unit. */
  var existingShipments = loadLS("gfi-shipments", []);
  var lotSet = {};
  existingShipments.forEach(function (s) { if (s.lot) lotSet[String(s.lot)] = true; });

  /* Cert lookup is local to the SHIPMENTS block — by the time we reach
     it, the Export Certs block has already populated `existingCerts`,
     but it's only used here to resolve line.exportCertNumber to a real
     cert id. The shipments block runs BEFORE export-certs by design
     (shipments are the higher-frequency entity), so the cert list here
     is the pre-import snapshot — anything imported in the same batch
     becomes resolvable on the next import. */
  var certList = loadLS("gfi-export-certs", []);
  function findCert(num) {
    if (!num) return null;
    var n = nrm(num);
    return certList.find(function (c) { return nrm(c.certNumber) === n; }) || null;
  }

  /* Group rows by lot, preserving first-seen order for stable output. */
  var lotGroups = {}; var lotOrder = [];
  parsed.shipments.forEach(function (r) {
    if (!r.lot) { summary.skipped++; return; }
    var key = String(r.lot);
    if (!lotGroups[key]) { lotGroups[key] = []; lotOrder.push(key); }
    lotGroups[key].push(r);
  });

  lotOrder.forEach(function (lotKey) {
    if (lotSet[lotKey]) { summary.skipped++; return; }
    var group = lotGroups[lotKey];
    var head = group[0];
    var sup = findSupplier(head.shipperName);
    var wh = findWarehouse(head.warehouse);
    var dep = head.dateDeparture || "";
    var arr = head.dateArrival || dep;
    var season = findSeason(arr);

    var lines = group.map(function (r) {
      var category = r.category === "Organic" || r.category === "OG" ? "OG"
        : r.category === "Conventional" || r.category === "CV" ? "CV"
        : r.category;
      var cert = findCert(r.exportCert);
      return {
        productBase: r.productName,
        productName: r.productName,
        /* presentation overrides Description when both present */
        productDescription: r.presentation || r.productDescription || "",
        category: category,
        cases: r.cases, pallets: r.pallets,
        price: r.reportedPrice, reportedPrice: r.reportedPrice,
        total: (parseFloat(r.cases) || 0) * (parseFloat(r.reportedPrice) || 0),
        saleStatus: r.saleStatus || "",
        exportCertId: cert ? cert.id : "",
        exportCertNumber: r.exportCert || (cert ? cert.certNumber : ""),
        exportCertManual: cert ? "" : (r.exportCert || ""),
      };
    });

    var shipment = {
      id: nextShipmentId(existingShipments),
      lot: head.lot,
      seasonId: season ? season.id : "",
      dateDeparture: dep,
      dateArrival: arr,
      week: isoWeek(arr),
      supplierId: sup ? sup.id : "",
      shipperName: sup ? (sup.shortName || sup.fullName || sup.name || sup.companyName || head.shipperName) : head.shipperName,
      shipperInitials: head.shipperInitials || (sup ? (sup.initials || "") : ""),
      shipperSource: "shipper",
      proforma: head.proforma, shipperInvoice: head.shipperInvoice, entryNumber: head.entryNumber,
      brokerMXId: "", brokerMXName: "", brokerMXManual: "",
      brokerUSId: "", brokerUSName: "", brokerUSManual: "",
      warehouseId: wh ? wh.id : "",
      warehouseName: wh ? (wh.shortName || wh.fullName || wh.name || head.warehouse) : (head.warehouse || ""),
      transport: {}, status: "received",
      lines: lines, documents: [],
    };
    existingShipments.push(shipment);
    lotSet[lotKey] = true;
    summary.shipments++;
  });
  saveLS("gfi-shipments", existingShipments);

  /* EXPORT CERTIFICATES — Dedup by certNumber. Resolves shipperName to
     supplierId. Status: expired if periodTo < today else active. The
     companion "Assigned Invoices" sheet appends entries to each cert's
     usedInvoices array; the live computeCertUsage() in
     ExportCertificateRegistry unions those with shipment-derived ones. */
  var existingCerts = loadLS("gfi-export-certs", []);
  var certByNumber = {};
  existingCerts.forEach(function (c) { if (c.certNumber) certByNumber[c.certNumber] = c; });

  var todayISO = new Date().toISOString().substring(0, 10);
  (parsed.exportCerts || []).forEach(function (r) {
    if (!r.certNumber) { summary.skipped++; return; }
    if (certByNumber[r.certNumber]) { summary.skipped++; return; }
    var sup = findSupplier(r.shipperName);
    var status = r.periodTo && r.periodTo < todayISO ? "expired" : "active";
    var total = parseInt(r.totalInvoices, 10) || 0;
    var cert = {
      id: nextCertId(existingCerts),
      certNumber: r.certNumber,
      supplierId: sup ? sup.id : "",
      shipperName: sup ? (sup.shortName || sup.fullName || sup.name || sup.companyName || r.shipperName) : r.shipperName,
      productName: r.product,
      productSource: "shipper",
      certifierName: r.certifyingBody,
      importer: "Ground Fresh International, Inc.",
      periodFrom: r.periodFrom,
      periodTo: r.periodTo,
      totalInvoices: total,
      usedInvoices: [],
      status: status,
      document: null,
      notes: r.notes,
    };
    existingCerts.push(cert);
    certByNumber[r.certNumber] = cert;
    summary.exportCerts++;
  });
  /* Append Assigned Invoices entries to their cert. Lookup uses the
     just-updated certByNumber map so newly imported certs and pre-
     existing ones both pick up entries. */
  (parsed.assignedInvoices || []).forEach(function (a) {
    if (!a.certNumber) { summary.skipped++; return; }
    var cert = certByNumber[a.certNumber];
    if (!cert) { summary.skipped++; return; }
    cert.usedInvoices = cert.usedInvoices || [];
    cert.usedInvoices.push({
      shipperInvoice: a.shipperInvoice || "",
      date: a.date || "",
      lot: "",
      shipmentId: "",
    });
  });
  saveLS("gfi-export-certs", existingCerts);

  /* SHIPMENT BILLS — multi-line aware (same grouping pattern as
     Shipments). All Excel rows that share the same Lot fold into ONE
     gfi-bills record with one product line per row. Header fields
     (Lot, Shipper, Bill Date, Due Date, Proforma, Shipper Invoice,
     Entry, Export Cert, Reference, Commission rate) come from the
     FIRST row of the group; per-row fields (Product, Category, Cases,
     Reported Price, Sale Status, Amount) become individual lines; per-
     row Fee 1-3 slots and the companion "Extra Fees" sheet contribute
     to the bill's fees array. supplierId is resolved against gfi-
     suppliers; shipmentId is resolved against gfi-shipments (now
     including the just-imported shipments). Status forced to "paid"
     with balance 0; a matching outbound payment record is created in
     gfi-payments when totalPaid metadata isn't applicable (advances
     are tracked via lessAmountPaid on the bill itself). Dedup by lot. */
  var existingBills = loadLS("gfi-bills", []);
  var billLotSet = {};
  existingBills.forEach(function (b) { if (b.lot) billLotSet[String(b.lot)] = true; });

  /* Group bill rows by lot, preserving order. */
  var billGroups = {}; var billOrder = [];
  (parsed.bills || []).forEach(function (r) {
    if (!r.lot) { summary.skipped++; return; }
    var key = String(r.lot);
    if (!billGroups[key]) { billGroups[key] = []; billOrder.push(key); }
    billGroups[key].push(r);
  });
  /* Pre-index Extra Fees by lot for O(1) lookup. */
  var extraFeesByLot = {};
  (parsed.extraFees || []).forEach(function (ef) {
    if (!ef.lot) return;
    var k = String(ef.lot);
    if (!extraFeesByLot[k]) extraFeesByLot[k] = [];
    extraFeesByLot[k].push(ef);
  });

  /* Newly imported shipments are already in `existingShipments`. Use
     that array for lot→shipment lookup so bills can pull shipmentId,
     supplierId etc. without re-reading localStorage. */
  function findShipmentByLot(lot) {
    if (!lot) return null;
    return existingShipments.find(function (s) { return String(s.lot) === String(lot); }) || null;
  }

  /* Resolve a bill line's productDescription. Priority:
     1. Explicit Excel column on the bill row (Product Description /
        Presentation / Descripción del Producto).
     2. Matching line on the parent shipment (same lot, same product
        name, same category) — bills are derived from shipments so the
        shipment's full presentation string is the source of truth.
     3. Catalog lookup in gfi-products: match by product name +
        category. If a single product matches, use its built
        description ("Blackberry 12/6 oz OG GF Label"). Multiple
        matches → leave blank rather than guess (could be wrong size /
        label). */
  var CAT_TO_CODE = { OG: "OG", CV: "CV", Organic: "OG", Conventional: "CV", organic: "OG", conventional: "CV" };
  function productCatalogDesc(productName, category) {
    if (!productName) return "";
    var pn = String(productName).trim().toLowerCase();
    var cc = CAT_TO_CODE[category] || category || "";
    var hits = existingProducts.filter(function (p) {
      if (String(p.product || "").trim().toLowerCase() !== pn) return false;
      if (!cc) return true;
      return (CAT_TO_CODE[p.category] || p.category) === cc;
    });
    if (hits.length !== 1) return "";
    var p = hits[0];
    if (p.description) return p.description;
    return [p.product, p.unitsPerCase + "/" + p.size, CAT_TO_CODE[p.category] || p.category, p.labelInitials, "Label"].filter(Boolean).join(" ");
  }
  function resolveBillLineDesc(r, shp) {
    if (r.productDescription) return r.productDescription;
    if (shp && shp.lines && shp.lines.length > 0) {
      var pn = String(r.productName || "").trim().toLowerCase();
      var cat = r.category;
      var ccBill = CAT_TO_CODE[cat] || cat || "";
      var shpLine = shp.lines.find(function (sl) {
        var slPN = String(sl.productName || sl.productBase || "").trim().toLowerCase();
        if (slPN !== pn) return false;
        var slCat = sl.category || "";
        if (!ccBill || !slCat) return true;
        return (CAT_TO_CODE[slCat] || slCat) === ccBill;
      });
      if (shpLine && shpLine.productDescription) return shpLine.productDescription;
    }
    return productCatalogDesc(r.productName, r.category);
  }

  billOrder.forEach(function (lotKey) {
    if (billLotSet[lotKey]) { summary.skipped++; return; }
    var group = billGroups[lotKey];
    var head = group[0];
    var sup = findSupplier(head.shipperName);
    var shp = findShipmentByLot(head.lot);
    /* dueDate: explicit wins; else date + supplier term (fallback 21). */
    var dueDate = head.dueDate;
    if (!dueDate && head.date) {
      var supDays = parseTermDays(sup && sup.paymentTerm);
      if (supDays === null) supDays = 21;
      dueDate = addDaysToISO(head.date, supDays);
    }

    /* Lines: one per row. Amount = explicit OR cases × reportedPrice. */
    var lines = group.map(function (r) {
      var category = r.category === "Organic" || r.category === "OG" ? "OG" : r.category === "Conventional" || r.category === "CV" ? "CV" : r.category;
      var cert = findCert(r.exportCert);
      var amt = (r.amount !== undefined && r.amount !== "") ? num(r.amount) : ((parseFloat(r.cases) || 0) * (parseFloat(r.reportedPrice) || 0));
      return {
        productBase: r.productName,
        productName: r.productName,
        productDescription: resolveBillLineDesc(r, shp),
        category: category,
        cases: r.cases, pallets: 0,
        reportedPrice: r.reportedPrice,
        total: amt,
        saleStatus: r.saleStatus || "",
        exportCertId: cert ? cert.id : "",
        exportCertNumber: r.exportCert || (cert ? cert.certNumber : ""),
        exportCertManual: cert ? "" : (r.exportCert || ""),
      };
    });

    /* Compute gross BEFORE fees so the Advance Financing Fee branch can
       resolve its amount from a percentage of gross (matches what the
       Bill form does interactively when that fee type is picked). */
    var gross = lines.reduce(function (s, l) { return s + (parseFloat(l.total) || 0); }, 0);

    /* Fees: union of per-row fee slots and Extra Fees sheet entries.
       The Advance Financing Fee row in the Excel typically carries only
       a Rate (e.g. 3 = 3% of gross) with blank Amount. The form treats
       that fee specially via `pctRate` → `amount = gross × pctRate/100`.
       We replicate that here so the imported fee lands with the right
       dollar amount AND the form's % input renders pre-populated. */
    function buildFee(fe) {
      var ftName = fe.feeType || "";
      var qty = fe.qty;
      var rate = fe.rate;
      var amount = fe.amount;
      var pctRate = "";
      /* Whitespace/case-tolerant match so cells like "Advance Financing Fee "
         or "advance financing fee" still trigger the percentage branch
         when needed. The branch ONLY runs when Fee Amount is blank/0;
         an explicit Fee Amount always wins (no recalc), per the
         FIX-1 contract. */
      var isFinancingType = String(ftName).trim().toLowerCase() === "advance financing fee";
      if (isFinancingType && (!amount || amount === 0) && rate) {
        /* Excel rate is the percentage (3 → 3%), amount derives from gross. */
        pctRate = String(rate);
        amount = gross * (parseFloat(rate) / 100);
      } else if ((!amount || amount === 0) && qty && rate) {
        amount = qty * rate;
      }
      return { feeType: ftName, feeCustom: "", qty: qty, rate: rate, amount: amount, pctRate: pctRate };
    }
    var fees = [];
    group.forEach(function (r) {
      (r.fees || []).forEach(function (fe) { fees.push(buildFee(fe)); });
    });
    (extraFeesByLot[lotKey] || []).forEach(function (ef) {
      fees.push(buildFee(ef));
    });

    /* Commission. Use explicit commissionAmount when given;
       else commissionRate × gross. */
    var commissionRate = head.commissionRate || 0;
    var commissionAmt = (head.commissionAmount !== undefined && head.commissionAmount !== "") ? num(head.commissionAmount) : (gross * (commissionRate / 100));
    var feesTotal = fees.reduce(function (s, fe) { return s + (parseFloat(fe.amount) || 0); }, 0);
    var subtotal = gross - commissionAmt - feesTotal;
    /* `amountPaid` is what the bill form labels as "Less Amount Paid".
       Importer takes it ONLY from the explicit Excel column; it never
       fabricates a settled value from subtotal. Empty column → empty
       amountPaid. Status can still be "paid" per the import contract,
       but no fictional money is recorded. */
    var amountPaid = (head.lessAmountPaid && head.lessAmountPaid > 0) ? head.lessAmountPaid : "";

    var bill = {
      id: nextBillId(existingBills),
      shipmentId: shp ? shp.id : "",
      lot: head.lot,
      billDate: head.date || "",
      dueDate: dueDate || "",
      supplierId: sup ? sup.id : (shp ? shp.supplierId : ""),
      shipperName: sup ? (sup.shortName || sup.fullName || sup.name || sup.companyName || head.shipperName) : (head.shipperName || (shp ? shp.shipperName : "")),
      proforma: head.proforma || (shp ? shp.proforma : ""),
      shipperInvoice: head.shipperInvoice || (shp ? shp.shipperInvoice : ""),
      entryNumber: head.entryNumber || (shp ? shp.entryNumber : ""),
      reference: head.reference || "",
      commissionRate: String(commissionRate),
      status: "paid",
      amountPaid: amountPaid,
      lines: lines,
      fees: fees,
      notes: "Imported from Excel — historical (paid)",
    };
    existingBills.push(bill);
    billLotSet[lotKey] = true;
    summary.bills++;
  });
  saveLS("gfi-bills", existingBills);

  /* INVOICES — multi-line aware. Rows that share the same PU# fold
     into ONE invoice with one line per row. Header fields (PU#, Date,
     Due Date, Client, Customer Ref, Lot, Shipper, fees, payment
     metadata) come from the first row of the group. Resolvers:
       - clientName → gfi-clients → clientId + clientInitials
       - lot → gfi-shipments → shipmentId + shipperName (if header
         didn't supply one)
       - lot → gfi-bills → billId
       - date → gfi-seasons → seasonId
     Payment metadata (totalPaid, paymentDate, paymentMethod,
     paymentReference) materializes one inbound entry in gfi-payments
     and appends to the invoice's .payments[] array. Status forced
     "paid" per the import contract. Dedup by puNumber. */
  var existingInvoices = loadLS("gfi-invoices", []);
  var existingPayments = loadLS("gfi-payments", []);
  var puSet = {};
  existingInvoices.forEach(function (inv) { if (inv.puNumber) puSet[String(inv.puNumber)] = true; });

  /* Group invoice rows by PU#. */
  var invGroups = {}; var invOrder = [];
  (parsed.invoices || []).forEach(function (r) {
    if (!r.puNumber) { summary.skipped++; return; }
    var key = String(r.puNumber);
    if (!invGroups[key]) { invGroups[key] = []; invOrder.push(key); }
    invGroups[key].push(r);
  });
  /* Pre-index Invoice Fees by PU# so each invoice's fee build is O(1). */
  var invoiceFeesByPu = {};
  (parsed.invoiceFees || []).forEach(function (ef) {
    if (!ef.puNumber) return;
    var k = String(ef.puNumber);
    if (!invoiceFeesByPu[k]) invoiceFeesByPu[k] = [];
    invoiceFeesByPu[k].push(ef);
  });

  function findBillByLot(lot) {
    if (!lot) return null;
    return existingBills.find(function (b) { return String(b.lot) === String(lot); }) || null;
  }
  /* Resolve a Fee → Bill reference. Accepts either an exact bill id
     ("BILL-0042") or a lot number ("57916") — bills are looked up both
     ways so the Excel author can use whichever is convenient. */
  function findBillByRef(ref) {
    if (!ref) return null;
    return existingBills.find(function (b) { return String(b.id) === String(ref) || String(b.lot) === String(ref); }) || null;
  }

  invOrder.forEach(function (puKey) {
    if (puSet[puKey]) { summary.skipped++; return; }
    var group = invGroups[puKey];
    var head = group[0];
    var client = findClient(head.clientName);
    var shp = findShipmentByLot(head.lot);
    var matchedBill = findBillByLot(head.lot);
    var season = findSeason(head.date);

    /* dueDate: explicit wins; else date + client term (fallback 21). */
    var dueDate = head.dueDate;
    if (!dueDate && head.date) {
      var cliDays = parseTermDays(client && client.paymentTerm);
      if (cliDays === null) cliDays = 21;
      dueDate = addDaysToISO(head.date, cliDays);
    }

    var lines = group.map(function (r) {
      var category = r.category === "Organic" || r.category === "OG" ? "OG" : r.category === "Conventional" || r.category === "CV" ? "CV" : r.category;
      return {
        productBase: r.productName,
        productName: r.productName,
        productDescription: r.productDescription || "",
        category: category,
        cases: r.cases, pallets: r.pallets,
        salePrice: r.salePrice,
        lot: r.lot,
        saleStatus: r.saleStatus || "",
      };
    });

    var gross = lines.reduce(function (s, l) { return s + ((parseFloat(l.cases) || 0) * (parseFloat(l.salePrice) || 0)); }, 0);

    /* Build the structured fees array. Three potential sources, all
       unioned (both inline + sheet can coexist; legacy single Fees
       column is a fallback only when the structured sources are empty):
         1. Inline Fee Type 1/2 slots from each row in the group.
         2. "Invoice Fees" sheet entries matched by PU#.
         3. Legacy single Fees / Deductions / Deducciones column ⇒ one
            "Imported Fees" line, only when 1 + 2 produced nothing. */
    var fees = [];
    group.forEach(function (r) {
      (r.fees || []).forEach(function (fe) {
        var linkedBill = fe.billRef ? findBillByRef(fe.billRef) : null;
        fees.push({ feeType: fe.feeType, feeCustom: "", qty: fe.qty, rate: fe.rate, amount: fe.amount, pctRate: "", linkedBillId: linkedBill ? linkedBill.id : "" });
      });
    });
    (invoiceFeesByPu[puKey] || []).forEach(function (ef) {
      var linkedBill = ef.billRef ? findBillByRef(ef.billRef) : null;
      fees.push({ feeType: ef.feeType, feeCustom: "", qty: ef.qty, rate: ef.rate, amount: ef.amount, pctRate: "", linkedBillId: linkedBill ? linkedBill.id : "" });
    });
    var legacyFeesTotal = head.feesTotal > 0 ? head.feesTotal : 0;
    if (fees.length === 0 && legacyFeesTotal > 0) {
      fees.push({ feeType: "Imported Fees", feeCustom: "", qty: 1, rate: legacyFeesTotal, amount: legacyFeesTotal, pctRate: "" });
    }
    var feesTotal = fees.reduce(function (s, fe) { return s + (parseFloat(fe.amount) || 0); }, 0);
    /* Commission: explicit Excel column wins; otherwise fall back to
       the client's default commissionRate (e.g. Twin River = 10). The
       cell value is already pctRate()-normalized so "10" and "10%"
       both arrive as 10. Single division here. */
    var commissionRateNum = parseFloat(head.commissionRate) || 0;
    if (!commissionRateNum && client && client.commissionRate) {
      commissionRateNum = parseFloat(client.commissionRate) || 0;
    }
    var commissionAmt = gross * (commissionRateNum / 100);
    /* Computed for completeness; not stored as a "paid" amount anymore.
       Importer no longer fabricates payments — the user records actual
       deposits manually after import via the PaymentRegistry flow. */
    var net = gross - commissionAmt - feesTotal; void net;
    var invoiceId = nextInvoiceId(existingInvoices);

    var invoice = {
      id: invoiceId,
      puNumber: head.puNumber,
      date: head.date,
      dueDate: dueDate || "",
      seasonId: season ? season.id : "",
      clientId: client ? client.id : "",
      clientName: client ? (client.shortName || client.fullName || client.name || head.clientName) : head.clientName,
      clientInitials: client ? (client.initials || "") : "",
      clientRef: head.clientRef,
      lot: head.lot,
      shipmentId: shp ? shp.id : "",
      billId: matchedBill ? matchedBill.id : "",
      shipperName: head.shipperName || (shp ? shp.shipperName : ""),
      /* commissionRate persisted as a string to mirror what the form's
         number input emits when the user edits it (parseFloat-friendly
         downstream in calcInvoiceTotal). Excel value wins; falls back
         to client.commissionRate when blank — matches the form's
         handleClientChange auto-pull behavior. */
      commissionRate: String(commissionRateNum || ""),
      /* Imported invoices land as DRAFT, not paid. Bulk status change
         in the registry locks records that have payments[]; an empty
         payments array keeps the record unlocked so the user can
         freely move it Draft → Sent → Paid as deposits arrive. */
      status: "draft",
      amountPaid: "",
      lines: lines,
      fees: fees,
      payments: [],
      lessAdvance: "", otherFees: "", lessCredits: "",
      remarks: "Imported from Excel",
    };
    existingInvoices.push(invoice);
    puSet[puKey] = true;
    summary.invoices++;
  });
  saveLS("gfi-invoices", existingInvoices);
  saveLS("gfi-payments", existingPayments);

  /* EXPENSE BILLS — flat structure (one record per Excel row). Up to 3
     fee/lot/bill triples per row; each fee is optionally linked to a
     shipment (via lot → gfi-shipments lookup) and a bill reference for
     distribution. dueDate: explicit wins; else date + 30. Status
     forced "paid" with balance 0. Dedup by vendor+reference+date. */
  var existingExpenses = loadLS("gfi-expense-bills", []);
  var expenseKeySet = {};
  existingExpenses.forEach(function (e) {
    var key = nrm(e.vendor) + "|" + nrm(e.reference) + "|" + (e.date || "");
    if (key !== "||") expenseKeySet[key] = true;
  });
  /* Pre-index Expense Fees sheet entries. The primary join key is
     reference + vendor (both lower-cased + trimmed) — the same composite
     used for dedup — so users can disambiguate when two vendors share a
     reference. When the sheet row only carries Reference, we also build
     a reference-only fallback bucket. */
  var expenseFeesByRefVendor = {};
  var expenseFeesByRef = {};
  (parsed.expenseFees || []).forEach(function (ef) {
    if (!ef.reference) return;
    var refKey = nrm(ef.reference);
    var compKey = refKey + "|" + nrm(ef.vendorName);
    if (ef.vendorName) {
      if (!expenseFeesByRefVendor[compKey]) expenseFeesByRefVendor[compKey] = [];
      expenseFeesByRefVendor[compKey].push(ef);
    } else {
      if (!expenseFeesByRef[refKey]) expenseFeesByRef[refKey] = [];
      expenseFeesByRef[refKey].push(ef);
    }
  });

  function buildExpenseFee(fe, rCategory) {
    var feeShp = fe.lot ? findShipmentByLot(fe.lot) : null;
    var feeBill = fe.billRef ? existingBills.find(function (b) { return String(b.id) === String(fe.billRef) || String(b.lot) === String(fe.billRef); }) : (fe.lot ? findBillByLot(fe.lot) : null);
    return {
      feeType: fe.feeType,
      feeCustom: "",
      qty: fe.qty || 1,
      rate: fe.rate || 0,
      amount: fe.amount || 0,
      shipmentId: feeShp ? feeShp.id : "",
      billId: feeBill ? feeBill.id : "",
      pctRate: "",
    };
  }

  (parsed.expenseBills || []).forEach(function (r) {
    if (!r.vendorName && !r.reference && !r.date) { summary.skipped++; return; }
    var key = nrm(r.vendorName) + "|" + nrm(r.reference) + "|" + (r.date || "");
    if (expenseKeySet[key]) { summary.skipped++; return; }
    var dueDate = r.dueDate || (r.date ? addDaysToISO(r.date, 30) : "");

    /* Union inline fee triples + Expense Fees sheet entries. The sheet
       lookup tries reference+vendor first (most specific), then
       reference alone, so users can use either format. Both sources
       coexist freely. */
    var inlineFees = (r.fees || []).map(function (fe) { return buildExpenseFee(fe, r.category); });
    var refKey = nrm(r.reference);
    var compKey = refKey + "|" + nrm(r.vendorName);
    var sheetSource = expenseFeesByRefVendor[compKey] || expenseFeesByRef[refKey] || [];
    var sheetFees = sheetSource.map(function (fe) { return buildExpenseFee(fe, r.category); });
    var fees = inlineFees.concat(sheetFees);
    /* Distribution snapshot mirrors the form's save logic in
       ExpenseBillRegistry. */
    var distribution = fees.filter(function (fe) { return fe.shipmentId; }).map(function (fe) {
      var feeShp = existingShipments.find(function (s) { return String(s.id) === String(fe.shipmentId); });
      return {
        shipmentId: fe.shipmentId,
        lot: feeShp ? feeShp.lot : "",
        pallets: "",
        costRate: "",
        amount: fe.amount,
        category: r.category || "other",
      };
    });
    var linkedLots = [...new Set(distribution.map(function (d) { return d.shipmentId; }))];

    var totalAmount = fees.reduce(function (s, fe) { return s + (parseFloat(fe.amount) || 0); }, 0);
    var expense = {
      id: nextExpenseBillId(existingExpenses),
      vendor: r.vendorName,
      reference: r.reference,
      category: r.category || "other",
      date: r.date || "",
      dueDate: dueDate,
      entryNumber: "",
      notes: "Imported from Excel — historical (paid)",
      payments: totalAmount > 0 ? [{ date: r.date || "", amount: String(totalAmount.toFixed(2)) }] : [],
      fees: fees,
      distribution: distribution,
      linkedLots: linkedLots,
      amount: totalAmount.toFixed(2),
    };
    existingExpenses.push(expense);
    expenseKeySet[key] = true;
    summary.expenseBills++;
  });
  saveLS("gfi-expense-bills", existingExpenses);

  return summary;
}

/* ───────────────── component ───────────────── */
export default function ImportFromExcel({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang];
  var [file, setFile] = useState(null);
  var [parsed, setParsed] = useState(null);
  var [verificationRows, setVerificationRows] = useState(0);
  var [seasonId, setSeasonId] = useState("");
  var [importing, setImporting] = useState(false);
  var [summary, setSummary] = useState(null);
  var [error, setError] = useState("");
  var inputRef = useRef(null);

  var seasons = useMemo(function () { return loadLS("gfi-seasons", []); }, []);

  var processFile = function (files) {
    var f = files && files[0];
    if (!f) return;
    setFile(f);
    setSummary(null); setError("");
    var reader = new FileReader();
    reader.onload = function (ev) {
      try {
        var wb = XLSX.read(ev.target.result, { type: "array", cellDates: false });
        /* Each template ships with a subset of sheets — readRows returns
           [] for missing sheets so a single Excel can drive any
           combination (Clients only, ExportCerts + AssignedInvoices,
           Shipments + Bills, etc.). */
        var clientsRows = readRows(wb, "Clients");
        var productsRows = readRows(wb, "Products");
        var shipmentsRows = readRows(wb, "Shipments");
        var invoicesRows = readRows(wb, "Invoices");
        var billsRows = readRows(wb, "Bills");
        var extraFeesRows = readRows(wb, "Extra Fees");
        var expenseBillsRows = readRows(wb, "Expense Bills");
        var expenseFeesRows = readRows(wb, "Expense Fees");
        var invoiceFeesRows = readRows(wb, "Invoice Fees");
        var exportCertsRows = readRows(wb, "Export Certs");
        if (exportCertsRows.length === 0) exportCertsRows = readRows(wb, "Export Certificates");
        var assignedInvoicesRows = readRows(wb, "Assigned Invoices");
        var verifRows = readRows(wb, "Verification");
        setParsed({
          clients: parseClientsRows(clientsRows),
          products: parseProductsRows(productsRows),
          shipments: parseShipmentsRows(shipmentsRows),
          invoices: parseInvoicesRows(invoicesRows),
          invoiceFees: parseInvoiceFeesRows(invoiceFeesRows),
          bills: parseBillsRows(billsRows),
          extraFees: parseExtraFeesRows(extraFeesRows),
          expenseBills: parseExpenseBillsRows(expenseBillsRows),
          expenseFees: parseExpenseFeesRows(expenseFeesRows),
          exportCerts: parseExportCertsRows(exportCertsRows),
          assignedInvoices: parseAssignedInvoicesRows(assignedInvoicesRows),
        });
        setVerificationRows(verifRows.length);
      } catch (err) {
        console.error(err);
        setError(t.parseError);
        setParsed(null);
      }
    };
    reader.readAsArrayBuffer(f);
  };
  var handleFile = function (e) { processFile(e.target.files); };

  var handleImport = function () {
    if (!parsed) { setError(t.noFile); return; }
    setImporting(true);
    /* Run on next tick so the spinner repaints before the (synchronous)
       localStorage writes block the main thread. */
    setTimeout(async function () {
      try {
        var s = importAll(parsed, seasonId);
        setSummary(s);
        /* Push the freshly-imported localStorage data up to Supabase so
           the cloud matches what the user just imported. Failures here
           don't roll back the import — localStorage already has the
           records and the user can retry via Settings → Migration. */
        try { await exportLocalToSupabase(); } catch (e) { console.error("[Import] supabase sync failed", e); }
      } catch (err) {
        console.error(err);
        setError(String(err.message || err));
      } finally {
        setImporting(false);
      }
    }, 50);
  };

  var handleClose = function () { if (window.__gfiNavigate) window.__gfiNavigate("dashboard"); };

  var Card = function (props) {
    return (
      <div style={{ padding: 14, background: "var(--color-bg)", borderRadius: 10, border: "1px solid var(--color-border)", textAlign: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: "var(--color-accent)", fontFamily: "'JetBrains Mono', monospace" }}>{props.value}</div>
        <div style={{ fontSize: 10, color: "var(--color-text-muted)", fontWeight: 600, marginTop: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{props.label}</div>
      </div>
    );
  };

  return (
    <DraggableModal title={t.title} onClose={handleClose} initialWidth={Math.min(1200, Math.floor(window.innerWidth * 0.85))} initialHeight={Math.min(900, window.innerHeight - 60)}>
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
        <div style={{ fontSize: 12, color: "var(--color-text-muted)", marginBottom: 18 }}>{t.subtitle}</div>

        {/* File picker */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>{t.pickFile}</label>
          <FileDropZone onFiles={processFile} multiple={false} lang={lang} style={{ padding: 4 }}>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <button onClick={function () { if (inputRef.current) inputRef.current.click(); }} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{"📂"} Choose file…</button>
              <span style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{file ? t.picked + ": " + file.name : (lang === "es" ? "— o arrastra el archivo aquí" : "— or drag the file here")}</span>
              <input ref={inputRef} type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: "none" }} />
            </div>
          </FileDropZone>
        </div>

        {/* Season selector */}
        <div style={{ marginBottom: 18 }}>
          <label style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--color-text-secondary)", letterSpacing: "0.06em", display: "block", marginBottom: 6 }}>{t.season}</label>
          <SearchSelect
            value={seasonId}
            onChange={setSeasonId}
            placeholder={t.noSeason}
            style={{ minWidth: 320 }}
            options={seasons.map(function (s) {
              var name = s.seasonName || ((s.startDate || "").substring(0, 4) + "–" + (s.endEstimate || "").substring(2, 4));
              return { value: s.id, label: name };
            })}
          />
        </div>

        {/* Preview */}
        {parsed && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--color-accent)", letterSpacing: "0.06em", marginBottom: 10 }}>{t.preview}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <Card label={t.clients} value={parsed.clients.length} />
              <Card label={t.products} value={parsed.products.length} />
              <Card label={t.shipments} value={parsed.shipments.length} />
              <Card label={t.exportCerts} value={parsed.exportCerts.length} />
              <Card label={t.bills} value={parsed.bills.length} />
              <Card label={t.invoices} value={parsed.invoices.length} />
              <Card label={t.expenseBills} value={parsed.expenseBills.length} />
              <Card label={t.verification} value={verificationRows} />
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ padding: "10px 14px", borderRadius: 6, background: "rgba(231,76,60,0.08)", border: "1px solid rgba(231,76,60,0.25)", color: "#DC2626", fontSize: 12, fontWeight: 600, marginBottom: 14 }}>{error}</div>
        )}

        {/* Summary */}
        {summary && (
          <div style={{ padding: 16, borderRadius: 10, background: "rgba(16,185,129,0.06)", border: "1px solid rgba(16,185,129,0.25)" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#059669", marginBottom: 10 }}>{"✓"} {t.summaryTitle}</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
              <Card label={t.clients} value={summary.clients} />
              <Card label={t.products} value={summary.products} />
              <Card label={t.shipments} value={summary.shipments} />
              <Card label={t.exportCerts} value={summary.exportCerts} />
              <Card label={t.bills} value={summary.bills} />
              <Card label={t.invoices} value={summary.invoices} />
              <Card label={t.expenseBills} value={summary.expenseBills} />
              <Card label={t.skipped} value={summary.skipped} />
            </div>
            <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 10 }}>
              {t.imported}: {summary.clients} clients · {summary.products} products · {summary.shipments} shipments · {summary.exportCerts} certs · {summary.bills} bills · {summary.invoices} invoices · {summary.expenseBills} expense bills. {t.skipped}: {summary.skipped}.
            </div>
          </div>
        )}
      </div>

      <div style={{ padding: "14px 24px", borderTop: "1px solid var(--color-border)", display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button onClick={handleClose} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>{summary ? t.close : t.cancel}</button>
        {!summary && (
          <button onClick={handleImport} disabled={!parsed || importing} style={{ padding: "8px 22px", borderRadius: 6, border: "none", background: !parsed || importing ? "var(--color-border)" : "var(--color-accent)", color: "var(--color-accent-text)", cursor: !parsed || importing ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700 }}>
            {importing ? t.importing : t.importBtn}
          </button>
        )}
      </div>
    </DraggableModal>
  );
}
